import React from "react";
import { useSelector } from "react-redux";
import {
    filterCategoriesBasedOnPresetsOrUserSelections,
    getCategoryFilteringFlags,
    hasActiveProductsToBeDisplayed,
} from "@qgiv/p2p-registration";
import { currencyString } from "@qgiv/core-js";
import { useLocation } from "react-router-dom";
import { ThemeType } from "../../../redux/constants";
import {
    selectCurrentPage,
    updateCurrentPage,
} from "../../../redux/slices/currentPageSlice";
import {
    addRegistration,
    selectAllRegistration,
    selectUnconfirmedRegistrations,
    updateCurrentRegistration,
    updateRegistration,
    updateRegistrationClassification,
    setCurrentRegistrationCategory,
} from "../../../redux/slices/registrationSlice";
import {
    selectHasParentRegistration,
    setAppShowRemainingForm,
} from "../../../redux/slices/appSettingsSlice";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";
import { selectAllProducts } from "../../../redux/slices/productsSlice";
import { selectThemeSettings } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectErroredEntities } from "../../../redux/slices/raceConditionsSlice";
import {
    selectAlias,
    selectAllP2pSettings,
    selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
    selectBaseUrl,
} from "../../../redux/slices/p2pSettingsSlice";
import { selectAllCategories } from "../../../redux/slices/categoriesSlice";
import { selectTeams } from "../../../redux/slices/teamsSlice";
import { selectAllFields } from "../../../redux/slices/fieldsSlice";
import {
    selectAllEmailVerification,
    updateLastVerifiedEmail,
    updateEmailVerificationInFlight,
    updateEmailHasBeenVerified,
    updateEmailExistsInCurrentRegistration,
    updateEmailExistsInPreviousRegistration,
    updateScrollToEmailField,
    updateYourFundraisingDashboard,
} from "../../../redux/slices/emailVerificationSlice";
import RegistrationPage from "./RegistrationPage";
import { selectParentRegistration } from "../../../redux/slices/parentRegistrationSlice";

const ConnectedRegistrationPage = () => {
    const location = useLocation();
    const currentPage = useSelector(selectCurrentPage);
    const { remainingUnconfirmedRegistrations } = useSelector(
        selectUnconfirmedRegistrations,
    );
    const alias = useSelector(selectAlias);

    const allowSubRegistrantToChangeRegRole = useSelector(
        selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
    );

    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const {
        classification,
        currentRegistration,
        allowEarlyFormSubmissionRegistrationPage,
        registrations = [],
    } = useSelector(selectAllRegistration);
    // Registration level stored classification
    const { Classification = [] } = classification;
    // Event level active classifications
    const classifications = useSelector(selectAllClassifications);
    const products = useSelector(selectAllProducts);
    const primaryRegistrationValues = registrations[0];
    const savedRegistrationValues = registrations[currentRegistration];
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.HEADER, state),
    );
    const {
        primaryRegProgressBar: { participantDetailsStepLabel = "Details" },
    } = themeSettings;
    const formSettings = useSelector(selectAllFormSettings);
    const { title, currency } = formSettings;
    const erroredEntities = useSelector(selectErroredEntities);
    const p2pSettings = useSelector(selectAllP2pSettings);
    const {
        enableClassifications,
        phoneFieldsType,
        minParticipantGoal,
        minTeamGoal,
        enableCommitments,
        enableIndividualRegistration,
        enableTeams,
    } = p2pSettings;
    const eventEnableCommitments = !!enableCommitments;
    const baseUrl = useSelector(selectBaseUrl);
    const redirect =
        enableClassifications &&
        classifications.length > 0 &&
        Classification.length < 1 &&
        !hasParentRegistration
            ? `${baseUrl}/form/classification`
            : undefined;
    const isAdditionalParticipant = currentRegistration > 0;
    const isChildParticipant =
        isAdditionalParticipant === true || hasParentRegistration === true;
    const categories = useSelector(selectAllCategories);

    const getCommitmentDetails = () => {
        let commitmentDetails = {};

        if (categories && Array.isArray(categories) && categories?.length > 0) {
            categories.forEach((category) => {
                const newCommitmentObjectForCategory = {
                    amount: category?.commitmentAmount,
                    deadline: category?.commitmentDeadline,
                    enabled:
                        category?.enableCommitments && eventEnableCommitments,
                };
                const categoryId = category.id;

                commitmentDetails = {
                    ...commitmentDetails,
                    [categoryId]: { ...newCommitmentObjectForCategory },
                };
            });
        }

        return commitmentDetails;
    };

    const commitmentDetails = getCommitmentDetails();

    const validationSettings = {
        currency,
        currencyString,
        commitmentDetails,
        eventEnableCommitments,
        phoneFieldsType,
        isChildParticipant,
        minParticipantGoal,
        minTeamGoal,
        categories,
    };
    const documentTitle = `${title} | ${participantDetailsStepLabel}`;

    const displayStore = hasActiveProductsToBeDisplayed(products);

    const teams = useSelector(selectTeams);
    const fields = useSelector(selectAllFields);
    const parentRegistration = useSelector(selectParentRegistration);

    const staticValuesForCategoryFlags = {
        enableClassifications,
        enableTeams,
        hasParentRegistration,
    };

    const {
        hasClassifications,
        hasPreSelectedTeamCaptain,
        hasPreSelectedTeamMember,
    } = getCategoryFilteringFlags({
        ...staticValuesForCategoryFlags,
        Classification,
        currentRegistration,
        values: primaryRegistrationValues,
    });

    const staticValuesForCategoryFilter = {
        allowSubRegistrantToChangeRegRole,
        categories,
        classifications,
        enableIndividualRegistration,
        enableTeams,
        hasParentRegistration,
        parentRegistration,
    };

    const categoriesFilteredBySettingsAndUserSelections =
        filterCategoriesBasedOnPresetsOrUserSelections({
            ...staticValuesForCategoryFilter,
            Classification,
            hasClassifications,
            hasPreSelectedTeamCaptain,
            hasPreSelectedTeamMember,
            isChildParticipant,
            registrations,
        });

    const settings = {
        categories,
        teams,
        formSettings,
        p2pSettings,
        fields,
        categoriesFilteredBySettingsAndUserSelections,
    };

    const hasMultipleRegistrations =
        registrations?.length > 0 &&
        registrations[1] &&
        Object.keys(registrations[1])?.length > 0;

    const getShouldUpdateAdditionalRegToMatchNewPrimaryTeam = (values) => {
        let shouldUpdateAdditionalRegToMatchNewPrimaryTeam = false;
        if (
            currentRegistration === 0 &&
            !allowSubRegistrantToChangeRegRole &&
            hasMultipleRegistrations &&
            values.Team !== primaryRegistrationValues.Team
        ) {
            shouldUpdateAdditionalRegToMatchNewPrimaryTeam = true;
        }
        return shouldUpdateAdditionalRegToMatchNewPrimaryTeam;
    };

    const emailVerification = useSelector(selectAllEmailVerification);

    const registrationPageProps = {
        allowSubRegistrantToChangeRegRole,
        alias,
        commitmentDetails,
        currentRegistration,
        currentPage,
        emailVerification,
        erroredEntities,
        hasParentRegistration,
        primaryRegistrationValues,
        remainingUnconfirmedRegistrations,
        getShouldUpdateAdditionalRegToMatchNewPrimaryTeam,
        registrations,
        // Store items viewable on Registration
        displayStore,
        products,
        redirect,
        savedRegistrationValues,
        settings,
        validationSettings,
        allowEarlyFormSubmissionRegistrationPage,
        baseUrl,
        // Dispatch functions
        addRegistration,
        updateCurrentRegistration,
        updateRegistration,
        updateRegistrationClassification,
        setCurrentRegistrationCategory,
        updateCurrentPage,
        setAppShowRemainingForm,
        updateLastVerifiedEmail,
        updateEmailVerificationInFlight,
        updateEmailHasBeenVerified,
        updateEmailExistsInCurrentRegistration,
        updateEmailExistsInPreviousRegistration,
        updateScrollToEmailField,
        updateYourFundraisingDashboard,
        // React router props
        location,
        documentTitle,
        // items needed to re-run category filter
        Classification,
        staticValuesForCategoryFilter,
        staticValuesForCategoryFlags,
        getCategoryFilteringFlags,
        filterCategoriesBasedOnPresetsOrUserSelections,
    };

    return <RegistrationPage {...registrationPageProps} />;
};

export default ConnectedRegistrationPage;
